import { economyFetch as fetch } from '../../../lib/economyClient';
/**
 * Betting System Server - トーナメント賭けサーバー連携
 *
 * Edge Function（tournament）と連携し、
 * 賭けの確定と配当金の請求を行う
 *
 * 設計方針: B方式（締切時一括反映）
 * - 賭けの追加/取り消しはローカルで処理
 * - 締切時にサーバーへ一括送信してゴールド減算
 * - 報酬受け取り時にサーバーへ送信してゴールド加算
 *
 * Created: 2025-12-22
 */

import { BET_TYPES } from './bettingSystem';

// TournamentTypeはtournamentManagerからエクスポートされていないため、ここで定義
export type TournamentType = 'daily' | 'weekly' | 'major';

// ========================================
// Supabase設定
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 30000;

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.access_token || null;
    }
  } catch (err) {
    console.error('[bettingSystemServer] Failed to get access token:', err);
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

export type BetType = 'win' | 'place' | 'exacta';

export interface BetItem {
  id: string;           // クライアント生成の賭けID
  type: BetType;
  target: string;       // NPC ID or "npc1-npc2"
  amount: number;       // 賭け金（G）
  odds: number;         // オッズ
}

export interface ConfirmedBet {
  client_id: string;
  server_id: string;
  amount: number;
  odds: number;
}

export interface FailedBet {
  client_id: string;
  reason: string;
}

export interface BetResult {
  server_id: string;
  type: BetType;
  target: string;
  amount: number;
  odds: number;
  is_win: boolean;
  payout: number;
}

export interface PayoutSummary {
  total_bets: number;
  won_count: number;
  total_bet_amount: number;
  total_payout: number;
  total_profit: number;
}

export interface PendingTournament {
  tournament_id: string;
  tournament_type: TournamentType;
  status: 'confirmed' | 'claimed';
  bets: Array<{
    server_id: string;
    client_id: string;
    type: BetType;
    target: string;
    amount: number;
    odds: number;
    result?: 'win' | 'lose';
    payout?: number;
  }>;
  total_amount: number;
  confirmed_at: number;
  claimed_at?: number;
  total_payout?: number;
}

// ========================================
// レスポンス型
// ========================================

export interface ConfirmBetsResponse {
  success: boolean;
  error?: string;
  code?: string;
  confirmed_bets?: ConfirmedBet[];
  failed_bets?: FailedBet[];
  total_amount?: number;
  new_gold_balance?: number;
}

export interface ClaimPayoutsResponse {
  success: boolean;
  error?: string;
  code?: string;
  results?: BetResult[];
  summary?: PayoutSummary;
  new_gold_balance?: number;
}

export interface BetStatusResponse {
  success: boolean;
  error?: string;
  pending_tournaments?: PendingTournament[];
}

// ========================================
// API関数
// ========================================

/**
 * サーバーが利用可能かチェック（認証済みかどうか）
 */
export function isBettingServerAvailable(): boolean {
  return getAccessToken() !== null;
}

/**
 * 賭けを一括確定（締切時に呼び出し）
 *
 * @param tournamentId - 大会ID
 * @param tournamentType - 大会タイプ（daily/weekly/major）
 * @param bets - 賭けリスト
 * @returns 確定結果
 */
export async function confirmBetsOnServer(
  tournamentId: string,
  tournamentType: TournamentType,
  bets: BetItem[]
): Promise<ConfirmBetsResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[confirmBetsOnServer] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated', code: 'AUTH_ERROR' };
    }

    if (!bets || bets.length === 0) {
      console.log('[confirmBetsOnServer] No bets to confirm');
      return { success: true, confirmed_bets: [], total_amount: 0 };
    }

    console.log('[confirmBetsOnServer] Confirming bets:', {
      tournamentId,
      tournamentType,
      betCount: bets.length,
    });

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/tournament/confirm-bets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          tournament_id: tournamentId,
          tournament_type: tournamentType,
          bets: bets.map(bet => ({
            id: bet.id,
            type: bet.type,
            target: bet.target,
            amount: bet.amount,
            odds: bet.odds,
          })),
        }),
      }),
      API_TIMEOUT_MS,
      'confirmBetsOnServer'
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[confirmBetsOnServer] Server error:', errorData);
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}`,
        code: errorData.code,
      };
    }

    const data: ConfirmBetsResponse = await response.json();
    console.log('[confirmBetsOnServer] Bets confirmed:', data);

    return data;
  } catch (err) {
    console.error('[confirmBetsOnServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to confirm bets',
    };
  }
}

/**
 * 配当金を一括請求（報酬受け取り時に呼び出し）
 *
 * @param tournamentId - 大会ID
 * @param result - 大会結果（優勝者、準優勝者）
 * @returns 払い戻し結果
 */
export async function claimPayoutsOnServer(
  tournamentId: string,
  result: { winner: string; second: string }
): Promise<ClaimPayoutsResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[claimPayoutsOnServer] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated', code: 'AUTH_ERROR' };
    }

    console.log('[claimPayoutsOnServer] Claiming payouts:', {
      tournamentId,
      result,
    });

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/tournament/claim-payouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          tournament_id: tournamentId,
          result,
        }),
      }),
      API_TIMEOUT_MS,
      'claimPayoutsOnServer'
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[claimPayoutsOnServer] Server error:', errorData);
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}`,
        code: errorData.code,
      };
    }

    const data: ClaimPayoutsResponse = await response.json();
    console.log('[claimPayoutsOnServer] Payouts claimed:', data);

    return data;
  } catch (err) {
    console.error('[claimPayoutsOnServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to claim payouts',
    };
  }
}

/**
 * 賭け状態を確認（リカバリ用）
 *
 * @returns 保留中の大会リスト
 */
export async function getBetStatus(): Promise<BetStatusResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[getBetStatus] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/tournament/bet-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }),
      API_TIMEOUT_MS,
      'getBetStatus'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data: BetStatusResponse = await response.json();
    console.log('[getBetStatus] Bet status:', data);

    return data;
  } catch (err) {
    console.error('[getBetStatus] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to get bet status',
    };
  }
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * ローカルの賭けリストをサーバー用の形式に変換
 */
export function convertLocalBetsToServerFormat(
  localBets: Array<{
    id: string;
    type: string;
    target: string;
    amount: number;
    odds: number;
  }>
): BetItem[] {
  return localBets.map(bet => ({
    id: bet.id,
    type: bet.type as BetType,
    target: bet.target,
    amount: bet.amount,
    odds: bet.odds,
  }));
}

/**
 * サーバーエラーコードを日本語メッセージに変換
 */
export function getErrorMessage(code?: string): string {
  switch (code) {
    case 'AUTH_ERROR':
      return '認証エラー: ログインしてください';
    case 'INSUFFICIENT_GOLD':
      return 'ゴールドが不足しています';
    case 'INVALID_BETS':
      return '無効な賭けが含まれています';
    case 'ALREADY_CONFIRMED':
      return 'この大会の賭けは既に確定済みです';
    case 'NO_BETS_FOUND':
      return 'この大会の賭けが見つかりません';
    case 'ALREADY_CLAIMED':
      return '配当金は既に受け取り済みです';
    default:
      return 'サーバーエラーが発生しました';
  }
}
